import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import InputField from 'components/styledFormElements/InputField'
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react'
import React from 'react'
import { Control, UseFormRegister } from 'react-hook-form'
import { CustomCheckoutFormValues } from 'containers/Protected/CustomCheckout/types'
import { getNestedErrorMessage } from 'containers/Protected/CustomCheckout/utils'
import IconUploader from './IconUploader'

interface PaymentMethodEditorProps {
  id: string
  index: number
  control: Control<CustomCheckoutFormValues>
  register: UseFormRegister<CustomCheckoutFormValues>
  errors: Record<string, any>
  expanded: boolean
  onToggleExpand: () => void
  name: string
  enabled: boolean
  onEnabledChange: (enabled: boolean) => void
}

const fieldContainerClass = 'flex w-full flex-col'

const PaymentMethodEditor: React.FC<PaymentMethodEditorProps> = ({
  id,
  index,
  control,
  register,
  errors,
  expanded,
  onToggleExpand,
  name,
  enabled,
  onEnabledChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  }

  const nameError = getNestedErrorMessage(errors, `bottomSheet.paymentMethods.${index}.name`)
  const iconError = getNestedErrorMessage(errors, `bottomSheet.paymentMethods.${index}.icon`)

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="w-full rounded-lg border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="flex items-center gap-2 px-6 py-4">
        <button
          type="button"
          className="cursor-grab rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-200"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder payment method"
        >
          <GripVertical size={16} />
        </button>

        <button
          type="button"
          onClick={onToggleExpand}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
              {name || `Payment Method ${index + 1}`}
            </p>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
              {enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => onEnabledChange(!enabled)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>

      </div>

      {expanded && (
        <div className="grid grid-cols-1 items-start gap-4 border-t border-gray-200 px-6 py-4 md:grid-cols-2 dark:border-slate-700">
          <InputField
            label="Name"
            required
            placeholder="e.g. Google Pay"
            containerClassName={fieldContainerClass}
            register={register(`bottomSheet.paymentMethods.${index}.name`)}
            error={nameError}
          />

          <IconUploader
            control={control}
            name={`bottomSheet.paymentMethods.${index}.icon`}
            error={iconError}
          />
        </div>
      )}
    </div>
  )
}

export default PaymentMethodEditor
